import React, { useState, useEffect } from "react";
import {
  X,
  Phone,
  Trash2,
  Calendar,
  CheckCircle,
  AlertCircle,
  Globe,
  Info,
  Clock,
} from "lucide-react";
import AdvancedFilterBuilder from "./AdvancedFilterBuilder";
import FilterPresetSelector from "./FilterPresetSelector";
import SaveFilterPresetModal from "./SaveFilterPresetModal";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

export const DAILY_FILTER_PREFIX = "__callback_daily__";

const CallbackDailyFilterModal = ({ isOpen, onClose, onSaved }) => {
  const { isDarkMode } = useTheme();
  const [filterConfig, setFilterConfig] = useState({
    logic: "AND",
    conditions: [],
  });
  const [regularPresets, setRegularPresets] = useState([]);
  const [activePreset, setActivePreset] = useState(null);
  const [selectedRegularPresetId, setSelectedRegularPresetId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      setStatus(null);
      const [presetsRes, userRes] = await Promise.all([
        apiService.getFilterPresets("lead"),
        apiService.getMe(),
      ]);
      const all = presetsRes.data || [];
      setCurrentUser(userRes.user);
      const daily = all.filter((p) => p.name?.startsWith(DAILY_FILTER_PREFIX));
      const regular = all.filter(
        (p) => !p.name?.startsWith(DAILY_FILTER_PREFIX),
      );
      setRegularPresets(regular);
      const active = daily.find((p) => p.isDefault);
      if (active) {
        setActivePreset(active);
        setFilterConfig(
          active.filterConfig || { logic: "AND", conditions: [] },
        );
      } else {
        setActivePreset(null);
        setFilterConfig({ logic: "AND", conditions: [] });
      }
      setSelectedRegularPresetId(null);
    } catch (err) {
      console.error("Error loading callback daily filter:", err);
      setStatus({ type: "error", message: "Failed to load filter data." });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRegularPreset = async (presetId) => {
    if (!presetId) {
      setSelectedRegularPresetId(null);
      setFilterConfig({ logic: "AND", conditions: [] });
      return;
    }
    try {
      const res = await apiService.getFilterPreset(presetId);
      const loaded = res.data.filterConfig || { logic: "AND", conditions: [] };
      if (!Array.isArray(loaded.conditions)) loaded.conditions = [];
      setSelectedRegularPresetId(presetId);
      setFilterConfig(loaded);
    } catch {
      setStatus({ type: "error", message: "Failed to load preset." });
    }
  };

  const handleDeleteRegularPreset = async (presetId) => {
    if (!window.confirm("Delete this filter preset?")) return;
    try {
      await apiService.deleteFilterPreset(presetId);
      setRegularPresets((p) => p.filter((x) => x.id !== presetId));
      if (selectedRegularPresetId === presetId) {
        setSelectedRegularPresetId(null);
        setFilterConfig({ logic: "AND", conditions: [] });
      }
    } catch {
      setStatus({ type: "error", message: "Failed to delete preset." });
    }
  };

  const handleSetDefaultRegularPreset = async (presetId) => {
    try {
      await apiService.updateFilterPreset(presetId, { isDefault: true });
      await loadData();
    } catch {
      setStatus({ type: "error", message: "Failed to set default." });
    }
  };

  const handleSaveNewPreset = async (presetData) => {
    if (!filterConfig.conditions?.length) {
      alert("Please add at least one condition before saving.");
      return;
    }
    try {
      const res = await apiService.createFilterPreset({
        ...presetData,
        entityType: "lead",
        filterConfig,
      });
      setRegularPresets((p) => [...p, res.data]);
      setShowSaveModal(false);
      setStatus({ type: "success", message: "Preset saved." });
    } catch (err) {
      console.error("Error saving preset:", err);
      throw err;
    }
  };

  const handleActivateFilter = async () => {
    if (!hasValidConditions()) return;
    try {
      setSaving(true);
      setStatus(null);
      const today = new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      const presetName = `${DAILY_FILTER_PREFIX}${today}`;
      let saved;
      if (activePreset) {
        await apiService.updateFilterPreset(activePreset.id, {
          name: presetName,
          filterConfig,
          isDefault: true,
          scope: "company",
          isPublic: true,
        });
        saved = { ...activePreset, filterConfig, name: presetName };
      } else {
        const res = await apiService.createFilterPreset({
          name: presetName,
          entityType: "lead",
          scope: "company",
          isPublic: true,
          isDefault: true,
          filterConfig,
        });
        saved = res.data;
      }
      setActivePreset(saved);
      setStatus({
        type: "success",
        message:
          "Daily callback filter activated — all users will see this applied to their Callbacks tab.",
      });
      if (onSaved) onSaved(filterConfig);
    } catch (err) {
      console.error("Error activating filter:", err);
      setStatus({ type: "error", message: "Failed to activate filter." });
    } finally {
      setSaving(false);
    }
  };

  const handleClearActiveFilter = async () => {
    if (!activePreset) return;
    if (
      !window.confirm(
        "Clear today's callback filter? All users will see their full callback list.",
      )
    )
      return;
    try {
      setSaving(true);
      await apiService.deleteFilterPreset(activePreset.id);
      setActivePreset(null);
      setFilterConfig({ logic: "AND", conditions: [] });
      setStatus({ type: "success", message: "Daily callback filter cleared." });
      if (onSaved) onSaved(null);
    } catch {
      setStatus({ type: "error", message: "Failed to clear filter." });
    } finally {
      setSaving(false);
    }
  };

  const hasValidConditions = () => {
    if (!Array.isArray(filterConfig?.conditions)) return false;
    if (filterConfig.conditions.length === 0) return false;
    return filterConfig.conditions.some((c) =>
      c.type === "group"
        ? c.conditions?.some((gc) => gc.field && gc.operator)
        : c.field && c.operator,
    );
  };

  if (!isOpen) return null;

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
        <div
          className={`w-full max-w-5xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden ${
            isDarkMode ? "bg-gray-900" : "bg-white"
          }`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between p-6 border-b ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Phone size={22} className="text-amber-400" />
              </div>
              <div>
                <h2
                  className={`text-xl font-bold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Daily Callback Filter
                  {activePreset && (
                    <span className="text-xs font-normal px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse inline-block" />
                      Active
                    </span>
                  )}
                </h2>
                <p
                  className={`text-sm mt-0.5 flex items-center gap-1.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  <Calendar size={13} />
                  {today}
                  <span
                    className={isDarkMode ? "text-gray-600" : "text-gray-300"}
                  >
                    ·
                  </span>
                  <Globe size={13} />
                  Applies globally to all users' Callbacks tab
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-800"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              <X size={22} />
            </button>
          </div>

          {/* Status banner */}
          {status && (
            <div
              className={`mx-6 mt-4 flex items-start gap-2 p-3 rounded-lg border text-sm ${
                status.type === "success"
                  ? "bg-green-500/10 border-green-500/30 text-green-400"
                  : "bg-red-500/10 border-red-500/30 text-red-400"
              }`}
            >
              {status.type === "success" ? (
                <CheckCircle size={15} className="mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
              )}
              {status.message}
            </div>
          )}

          {/* Info callout */}
          <div className="mx-6 mt-4 flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <Info size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-300/80">
              This filter narrows each user's own callback list — it does{" "}
              <strong>not</strong> reassign leads. Think of it as "today we're
              focusing on these." To redistribute callbacks between agents, use{" "}
              <strong>Auto-Assign Callbacks</strong> instead.
            </p>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
              </div>
            ) : (
              <>
                {/* Load from a regular saved preset */}
                <div className="space-y-2">
                  <label
                    className={`block text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Start from a Saved Filter Preset{" "}
                    <span
                      className={`font-normal ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      (optional)
                    </span>
                  </label>
                  <FilterPresetSelector
                    presets={regularPresets}
                    selectedPresetId={selectedRegularPresetId}
                    onSelectPreset={handleSelectRegularPreset}
                    onDeletePreset={handleDeleteRegularPreset}
                    onSetDefault={handleSetDefaultRegularPreset}
                    currentUserId={currentUser?.id}
                  />
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Selecting a preset loads it into the builder below. You can
                    then edit it before activating.
                  </p>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div
                      className={`w-full border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span
                      className={`px-3 ${isDarkMode ? "bg-gray-900 text-gray-500" : "bg-white text-gray-400"}`}
                    >
                      {selectedRegularPresetId
                        ? "Editing loaded preset"
                        : "Build filter"}
                    </span>
                  </div>
                </div>

                {/* Filter Builder */}
                <AdvancedFilterBuilder
                  onApply={(config) => {
                    setFilterConfig(config);
                    setSelectedRegularPresetId(null);
                  }}
                  onSave={() => {
                    if (!filterConfig.conditions?.length) {
                      alert("Add at least one condition before saving.");
                      return;
                    }
                    setShowSaveModal(true);
                  }}
                  initialFilters={filterConfig}
                  showSaveButton={true}
                />
              </>
            )}
          </div>

          {/* Footer */}
          <div
            className={`flex items-center justify-between p-5 border-t ${
              isDarkMode
                ? "border-gray-700 bg-gray-800/50"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-3">
              {activePreset && (
                <button
                  onClick={handleClearActiveFilter}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  Clear Today's Filter
                </button>
              )}
              {filterConfig.conditions?.length > 0 && (
                <span
                  className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  {filterConfig.conditions.length} condition
                  {filterConfig.conditions.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  isDarkMode
                    ? "text-gray-400 hover:text-white hover:bg-gray-700"
                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"
                }`}
              >
                Close
              </button>
              <button
                onClick={handleActivateFilter}
                disabled={!hasValidConditions() || saving}
                className="flex items-center gap-2 px-5 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors font-medium"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Activating...
                  </>
                ) : (
                  <>
                    <Clock size={15} />
                    {activePreset
                      ? "Update Today's Filter"
                      : "Activate for Today"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <SaveFilterPresetModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveNewPreset}
      />
    </>
  );
};

export default CallbackDailyFilterModal;
